// Comment text comes from the customer's Notion workspace and is untrusted;
// render as text, never markup. Authors arrive as bare Notion user ids; the
// backend does not resolve names. The discussion id is a reply target for the
// write tools.

import { isString } from "../utils/schemaUtils";

interface NotionCommentItem {
  id: string;
  authorId: string;
  createdTime: string;
  text: string;
}

export interface NotionCommentThread {
  discussionId: string;
  comments: NotionCommentItem[];
}

export interface NotionCommentsPayload {
  type: "notion_comments";
  pageId: string;
  // Absent when the page lookup failed, as it does for a block id.
  pageUrl?: string;
  discussions: NotionCommentThread[];
}

const isNotionCommentItem = (v: unknown): v is NotionCommentItem => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.id) && isString(r.authorId) && isString(r.createdTime) && isString(r.text);
};

const isNotionCommentThread = (v: unknown): v is NotionCommentThread => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.discussionId) && Array.isArray(r.comments) && r.comments.every(isNotionCommentItem);
};

export const parseNotionCommentsPayload = (v: unknown): NotionCommentsPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "notion_comments") return null;
  if (!isString(r.pageId)) return null;
  if (r.pageUrl !== undefined && !isString(r.pageUrl)) return null;
  if (!Array.isArray(r.discussions) || !r.discussions.every(isNotionCommentThread)) return null;
  return {
    type: "notion_comments",
    pageId: r.pageId,
    pageUrl: r.pageUrl,
    discussions: r.discussions,
  };
};
